import type { ReactNode } from 'react';

import { theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import checkIcon from 'assets/images/check-white.svg';

import { Typography } from './Typography';

const Root = styled.div`
  display: flex;
  width: 440px;
  max-width: 100%;
  padding: 0 20px 24px;
`;

// Progress contains: 1 bullet + 1 title + 1 advancement bar
const Progress = styled.div`
  flex: 1;

  &:last-of-type {
    flex: none;
  }

  display: flex;
`;

// Base styles for the progress bullets
const BaseProgressBullet = styled.span`
  --backgroundColor: ${theme.colors.neutral[100]};
  --borderColor: ${theme.colors.primary[300]};

  position: relative;
  display: block;
  height: 16px;
  width: 16px;
  min-width: 16px;
  border-radius: 50%;
  border: 1px solid var(--borderColor);
  background-color: var(--backgroundColor);
`;

const ProgressBulletCompleted = styled(BaseProgressBullet)`
  --backgroundColor: ${theme.colors.primary[300]};

  &::before {
    position: absolute;

    margin-left: 2px;

    font-size: 12px;
    line-height: 18px;
    color: ${theme.colors.neutral[100]};
  }
`;

const ProgressBulletCurrent = styled(BaseProgressBullet)`
  &::before {
    content: '';

    position: absolute;
    top: 2px;
    left: 2px;

    width: 10px;
    height: 10px;

    background-color: ${theme.colors.primary[300]};
    border-radius: 50%;
  }
`;

const ProgressBulletInactive = styled(BaseProgressBullet)`
  --borderColor: ${theme.colors.neutral['600']};
`;

type ProgressBulletProps = {
  children: ReactNode;
  status: 'completed' | 'current' | 'inactive';
};

// Complete progress bullet component
const ProgressBullet = ({ children, status }: ProgressBulletProps) => {
  if (status === 'completed') {
    return <ProgressBulletCompleted>{children}</ProgressBulletCompleted>;
  }

  if (status === 'current') {
    return <ProgressBulletCurrent>{children}</ProgressBulletCurrent>;
  }

  return <ProgressBulletInactive>{children}</ProgressBulletInactive>;
};

const CheckmarkImg = styled.img`
  position: absolute;
  top: 3px;
  left: 3px;
`;

// Category title
const Title = styled(Typography)`
  --noir-medium: ${theme.colors.primary[400]};
  --white-light: ${theme.colors.neutral[100]};
  --color: var(--noir-medium);
  --opacity: 0.5;

  position: absolute;
  left: 50%;
  transform: translate3d(-50%, 22px, 0);

  color: var(--color);
  opacity: var(--opacity);
`;

// Advancement bar
const Advancement = styled.span`
  position: relative;
  display: block;
  width: 100%;
  height: 4px;
  margin-top: 6px;
  background-color: ${theme.colors.neutral[100]};
  border: 1px solid ${theme.colors.neutral['600']};
  border-left: none;
  border-right: none;
`;

const AdvancementCompleted = styled.span`
  position: absolute;
  display: block;
  top: -1px;
  height: 4px;
  background-color: ${theme.colors.primary[300]};
`;

type StepsProps = {
  numberLabels?: boolean;
  questionIndex?: number;
  currentQuestion?: {
    name: string;
    category?: string;
  };
  questions: Array<{
    name: string;
    category?: string;
  }>;
  theme?: 'noir' | 'white';
};

export const Steps = ({
  currentQuestion,
  questions,
  theme: variant = 'noir',
  questionIndex = 0,
  numberLabels = false,
}: StepsProps) => {
  // In case of numberLabels, use stepper with number labels
  const currentCategoryName = numberLabels ? questionIndex : currentQuestion?.category;

  /* Same here, we offset the question index by 1, to make it look as if the second question is the first
    (true first question is the interstitial be we don't show the stepper on it) */
  const categoriesList = numberLabels
    ? questions.slice(1).map((_q, i) => i + 1)
    : questions
        .map((q) => q.category)
        .filter((value) => value)
        .filter((value, index, self) => self.indexOf(value) === index);

  const currentCategoryIndex = categoriesList.indexOf(currentCategoryName);

  const currentCategoryQuestions = questions.filter((q) => q.category === currentCategoryName);
  const indexOfCurrentQuestionInCategory = numberLabels
    ? questionIndex
    : currentCategoryQuestions.findIndex((q) => q.name === currentQuestion?.name);

  return (
    <Root>
      {categoriesList.map((renderedCategory, renderedCategoryIndex) => {
        const bulletStatus =
          currentCategoryIndex > renderedCategoryIndex
            ? 'completed'
            : currentCategoryIndex === renderedCategoryIndex
            ? 'current'
            : 'inactive';

        const advancementCompletedWidth =
          bulletStatus === 'completed'
            ? `100%`
            : bulletStatus === 'current'
            ? `${Math.max(
                0,
                Math.min(
                  100,
                  (indexOfCurrentQuestionInCategory / currentCategoryQuestions.length) * 100,
                ),
              )}%`
            : `0%`;

        return (
          <Progress
            key={`progress-${renderedCategory}`}
            data-testid={`progress-${renderedCategory}`}
          >
            <ProgressBullet status={bulletStatus}>
              {bulletStatus === 'completed' && (
                <CheckmarkImg
                  alt="Check icon - Progress completed - vector"
                  src={checkIcon}
                  width="8"
                />
              )}

              <Title
                style={{
                  '--color': variant === 'white' ? 'var(--white-light)' : 'var(--noir-medium)',
                  '--opacity': currentCategoryIndex === renderedCategoryIndex ? 1 : 0.5,
                }}
                variant="mono4"
              >
                {renderedCategory}
              </Title>
            </ProgressBullet>
            {renderedCategoryIndex < categoriesList.length - 1 && (
              <Advancement>
                <AdvancementCompleted style={{ width: advancementCompletedWidth }} />
              </Advancement>
            )}
          </Progress>
        );
      })}
    </Root>
  );
};
